import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { PATHS } from '@/common';

const QUERY_KEY = 'GET_IRS_LIMITS_QUERY_KEY';

interface EmployeePlanPair {
  employeeId?: string;
  planId?: string;
}

interface PlanIdToContributionAmountPayload {
  [planId: string]: number;
}
interface EmployeesPlansContributionPayload {
  employee_id?: number;
  plan_id_to_contribution_amount?: PlanIdToContributionAmountPayload[];
  plan_id_to_election_limit_amount?: PlanIdToContributionAmountPayload[];
}

interface PlanIdToContributionAmount {
  [planId: string]: number;
}
interface EmployeesPlansContribution {
  employeeId?: string;
  planIdToContributionAmount?: PlanIdToContributionAmount[];
}

const formatData = (data?: EmployeesPlansContributionPayload[]): EmployeesPlansContribution[] => data?.map((item) => ({
  employeeId: toString(item?.employee_id),
  ...item?.plan_id_to_contribution_amount ? {
    planIdToContributionAmount: item.plan_id_to_contribution_amount,
  } : {},
  ...item?.plan_id_to_election_limit_amount ? {
    planIdToElectionLimitAmount: item.plan_id_to_election_limit_amount,
  } : {},
})) || [];

interface IrsLimitRequest {
  employee_id: number;
  plan_id: number;
  tax_year: number;
}

export interface IrsLimitSearchRequest {
  requests: IrsLimitRequest[];
}

interface Props {
  taxYear?: string;
  employeePlanPairs?: EmployeePlanPair[];
}

export const useGetIrsLimitsQuery = (params?: Props) => {
  const searchRequest = useMemo<IrsLimitSearchRequest | null>(() => {
    if (!params?.employeePlanPairs?.length || !params?.taxYear) {
      return null;
    }

    const hasValidPairs = !params.employeePlanPairs.some((item) => !item?.employeeId || !item?.planId);
    if (!hasValidPairs) return null;

    return {
      requests: params.employeePlanPairs.map((pair) => ({
        employee_id: Number(pair.employeeId),
        plan_id: Number(pair.planId),
        tax_year: Number(params.taxYear),
      })),
    };
  }, [params]);

  const { data, isLoading, isSuccess, isError, refetch } = useQuery(
    [QUERY_KEY, searchRequest],
    () => api.post<EmployeesPlansContributionPayload[]>(PATHS.GET_IRS_LIMITS, searchRequest!),
    {
      enabled: !!searchRequest,
      retry: 0,
    }
  );

  const formattedData = useMemo<EmployeesPlansContribution[]>(() => formatData(data?.data || []), [data]);

  return {
    irsLimits: formattedData,
    isLoading,
    isSuccess,
    isError,
    refetch,
  };
};
